import React, { createContext, useContext, useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // ---------------- CHECK AUTH STATUS ----------------
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const storedUser = localStorage.getItem("user");
        if (token && storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
        localStorage.removeItem("user");
        localStorage.removeItem("authToken");
      } finally {
        setLoading(false);
      }
    };
    checkAuthStatus();
  }, []);

  // ---------------- REGISTER ----------------
  const register = async (userData) => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_REGISTER_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });
      const data = await response.json();

      if (response.ok) {
        setPendingUser(userData);
        setPendingVerification(true);
        return { success: true, data, requiresVerification: true };
      } else {
        const errorMessage = data.message || "Registration failed";
        return { success: false, error: errorMessage };
      }
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  // ---------------- VERIFY EMAIL ----------------
  const verifyEmail = async (verificationCode) => {
    try {
      if (!pendingUser) {
        throw new Error(
          "No pending verification found. Please register again."
        );
      }

      const verificationData = { ...pendingUser, code: verificationCode };

      const response = await fetch(API_ENDPOINTS.GET_EMAIL_VERIFY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(verificationData),
      });

      const data = await response.json();
      if (response.ok) {
        const loginResult = await login(
          pendingUser.email,
          pendingUser.password
        );
        if (loginResult.success) {
          setPendingVerification(false);
          setPendingUser(null);
          return { success: true, data };
        } else {
          throw new Error("Verification succeeded but auto-login failed.");
        }
      } else {
        const errorMessage = data.message || "Verification failed";
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ---------------- LOGIN ----------------
  const login = async (email, password) => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_LOGIN_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (response.ok) {
        const userData = {
          email: data.email || email,
          username: data.username,
          institution: data.institution,
          designation: data.designation,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("authToken", data.token || "dummy-token");
        return { success: true };
      } else {
        return { success: false, error: data.message || "Invalid credentials" };
      }
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  // ---------------- LOGOUT ----------------
  const logout = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (token && token !== "dummy-token") {
        await fetch(API_ENDPOINTS.GET_LOGOUT_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } finally {
      setUser(null);
      setPendingVerification(false);
      setPendingUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
    }
  };

  // ---------------- RESEND VERIFICATION (optional) ----------------
  const resendVerification = async () => {
    if (!pendingUser)
      return { success: false, error: "No pending verification found" };
    return {
      success: true,
      message: "If you didn’t receive the code, check spam or re-register.",
    };
  };

  // ---------------- PASSWORD RESET APIs ----------------
  const requestPasswordReset = async (email) => {
    try {
      const response = await fetch(API_ENDPOINTS.PASSWORD_RESET_REQUEST_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) return { success: true, message: data.message };
      return { success: false, error: data.message || "Request failed" };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  const verifyResetCode = async (email, code) => {
    try {
      const response = await fetch(API_ENDPOINTS.PASSWORD_RESET_VERIFY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await response.json();
      if (response.ok) return { success: true, message: data.message };
      return { success: false, error: data.message };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  const resetPassword = async (email, code, password) => {
    try {
      const response = await fetch(API_ENDPOINTS.PASSWORD_RESET_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, password }),
      });
      const data = await response.json();
      if (response.ok) return { success: true, message: data.message };
      return { success: false, error: data.message };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  };

  // ---------------- CONTEXT VALUE ----------------
  const value = {
    user,
    loading,
    register,
    verifyEmail,
    login,
    logout,
    resendVerification,
    pendingVerification,
    pendingUser,
    setPendingVerification,
    // Password Reset
    requestPasswordReset,
    verifyResetCode,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------- CUSTOM HOOK ----------------
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
